import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

const IDEMPOTENCY_KEY_PATTERN = "^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$";
const COMBINED_NUMBER_PATTERN = "^HD-[0-9]{8}-[0-9A-F]{12}$";
const SHA256_PATTERN = "^[a-f0-9]{64}$";

export const CombinedCheckoutManualPaymentMethodSchema = Type.Union([
  Type.Literal("CASH"),
  Type.Literal("STATIC_WECHAT"),
  Type.Literal("STATIC_ALIPAY"),
]);
export type CombinedCheckoutManualPaymentMethod = Static<
  typeof CombinedCheckoutManualPaymentMethodSchema
>;

export const CombinedCheckoutOrderRequestSchema = Type.Object(
  {
    sessionId: Type.String({ minLength: 1, maxLength: 128 }),
    orderId: Type.String({ minLength: 1, maxLength: 128 }),
    orderNumber: Type.String({ minLength: 1, maxLength: 128 }),
    tableId: Type.String({ minLength: 1, maxLength: 128 }),
    tableNumber: Type.String({ minLength: 1, maxLength: 64 }),
    expectedTableRevision: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    expectedAmountFen: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
  },
  { additionalProperties: false },
);
export type CombinedCheckoutOrderRequest = Static<typeof CombinedCheckoutOrderRequestSchema>;

export const CreateCombinedCheckoutRequestSchema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    orders: Type.Array(CombinedCheckoutOrderRequestSchema, { minItems: 2, maxItems: 4 }),
    expectedTotalFen: Type.Integer({ minimum: 2, maximum: Number.MAX_SAFE_INTEGER }),
    method: CombinedCheckoutManualPaymentMethodSchema,
    idempotencyKey: Type.String({ pattern: IDEMPOTENCY_KEY_PATTERN }),
  },
  { additionalProperties: false },
);
export type CreateCombinedCheckoutRequest = Static<typeof CreateCombinedCheckoutRequestSchema>;

export const CombinedCheckoutOrderAllocationSchema = Type.Object(
  {
    allocationOrder: Type.Integer({ minimum: 1, maximum: 4 }),
    sessionId: Type.String({ minLength: 1, maxLength: 128 }),
    orderId: Type.String({ minLength: 1, maxLength: 128 }),
    orderNumber: Type.String({ minLength: 1, maxLength: 128 }),
    tableId: Type.String({ minLength: 1, maxLength: 128 }),
    tableNumber: Type.String({ minLength: 1, maxLength: 64 }),
    businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
    amountFen: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    orderVersionBefore: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    orderVersionAfter: Type.Integer({ minimum: 2, maximum: Number.MAX_SAFE_INTEGER }),
    paymentId: Type.String({ minLength: 1, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type CombinedCheckoutOrderAllocation = Static<typeof CombinedCheckoutOrderAllocationSchema>;

export const CombinedCheckoutReadModelSchema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    combinedCheckoutId: Type.String({ minLength: 1, maxLength: 128 }),
    combinedNumber: Type.String({ pattern: COMBINED_NUMBER_PATTERN }),
    businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
    status: Type.Literal("COMPLETED"),
    method: CombinedCheckoutManualPaymentMethodSchema,
    totalFen: Type.Integer({ minimum: 2, maximum: Number.MAX_SAFE_INTEGER }),
    commandSha256: Type.String({ pattern: SHA256_PATTERN }),
    orders: Type.Array(CombinedCheckoutOrderAllocationSchema, { minItems: 2, maxItems: 4 }),
    manualReviewId: Type.Union([Type.String({ minLength: 1, maxLength: 128 }), Type.Null()]),
    createdBy: Type.String({ minLength: 1, maxLength: 128 }),
    createdAt: Type.String({ minLength: 20, maxLength: 32 }),
    evidence: Type.Literal("STORE_ACCEPTED"),
  },
  { additionalProperties: false },
);
export type CombinedCheckoutReadModel = Static<typeof CombinedCheckoutReadModelSchema>;

function safeSum(values: readonly number[]): number | null {
  let sum = 0;
  for (const value of values) {
    if (!Number.isSafeInteger(value)) return null;
    sum += value;
    if (!Number.isSafeInteger(sum)) return null;
  }
  return sum;
}

function unique(values: readonly string[]): boolean {
  return new Set(values).size === values.length;
}

function isCanonicalTimestamp(value: string): boolean {
  const epoch = Date.parse(value);
  return Number.isFinite(epoch) && new Date(epoch).toISOString() === value;
}

function ordersHaveCanonicalIdentity(
  orders: readonly CombinedCheckoutOrderRequest[],
  expectedTotalFen: number,
): boolean {
  if (
    !unique(orders.map((order) => order.sessionId)) ||
    !unique(orders.map((order) => order.orderId)) ||
    !unique(orders.map((order) => order.orderNumber)) ||
    !unique(orders.map((order) => order.tableId)) ||
    !unique(orders.map((order) => order.tableNumber))
  ) {
    return false;
  }
  for (let index = 1; index < orders.length; index += 1) {
    const previous = orders[index - 1];
    const current = orders[index];
    if (!previous || !current || previous.sessionId.localeCompare(current.sessionId) >= 0) {
      return false;
    }
  }
  return safeSum(orders.map((order) => order.expectedAmountFen)) === expectedTotalFen;
}

export function isCreateCombinedCheckoutRequest(
  value: unknown,
): value is CreateCombinedCheckoutRequest {
  return (
    Value.Check(CreateCombinedCheckoutRequestSchema, value) &&
    ordersHaveCanonicalIdentity(
      (value as CreateCombinedCheckoutRequest).orders,
      (value as CreateCombinedCheckoutRequest).expectedTotalFen,
    )
  );
}

export function isCombinedCheckoutReadModel(value: unknown): value is CombinedCheckoutReadModel {
  if (!Value.Check(CombinedCheckoutReadModelSchema, value)) return false;
  const result = value as CombinedCheckoutReadModel;
  if (
    !isCanonicalTimestamp(result.createdAt) ||
    !unique(result.orders.map((order) => order.sessionId)) ||
    !unique(result.orders.map((order) => order.orderId)) ||
    !unique(result.orders.map((order) => order.orderNumber)) ||
    !unique(result.orders.map((order) => order.tableId)) ||
    !unique(result.orders.map((order) => order.tableNumber)) ||
    !unique(result.orders.map((order) => order.paymentId)) ||
    result.orders.some((order, index) => {
      const previous = index > 0 ? result.orders[index - 1] : undefined;
      return (
        order.allocationOrder !== index + 1 ||
        order.orderVersionAfter !== order.orderVersionBefore + 1 ||
        (index > 0 && (!previous || previous.sessionId.localeCompare(order.sessionId) >= 0))
      );
    }) ||
    safeSum(result.orders.map((order) => order.amountFen)) !== result.totalFen
  ) {
    return false;
  }
  return result.method === "CASH" ? result.manualReviewId === null : result.manualReviewId !== null;
}
